import { formatMonthName } from "utility/formatters";

/*

type	                  event_type        Colloquial Name
------------            ---------------   ------------
Hedge Enhancer	        Down & Out (<=)   Knockout
Hedge Enhancer	        Floor             Floor-Ceiling
Hedge Enhancer	        Naked             Suspension
Accu KO Parachute	      Down & Out (<=)   Parachute

*/

export const getDoubleBehavior = (period, isAtExpiry) => (isAtExpiry ? "At Expiration" : period);

export const formatMaturity = (maturityStr) => {
  if (!maturityStr) return "";
  const [day, month] = maturityStr.split("-");
  const monthNum = new Date(`${month} 1, 2000`).getMonth() + 1;
  return `${formatMonthName(monthNum)} ${day}`;
};

export const OTC_PRODUCT_FLOOR_CEILING = "Floor-Ceiling";
export const OTC_PRODUCT_KNOCKOUT = "Knockout";
export const OTC_PRODUCT_PARACHUTE = "Parachute";
export const OTC_PRODUCT_SUSPENSION = "Suspension";

export const OTC_TYPE_FLOOR_CEILING = "Hedge Enhancer";
export const OTC_TYPE_KNOCKOUT = "Hedge Enhancer";
export const OTC_TYPE_PARACHUTE = "Accu KO Parachute";
export const OTC_TYPE_SUSPENSION = "Hedge Enhancer";

export const OTC_EVENT_TYPE_FLOOR_CEILING = "Floor";
export const OTC_EVENT_TYPE_KNOCKOUT = "Down & Out (<=)";
export const OTC_EVENT_TYPE_PARACHUTE = "Down & Out (<=)";
export const OTC_EVENT_TYPE_SUSPENSION = "Naked";

export const OTC_TYPES = [OTC_TYPE_FLOOR_CEILING, OTC_TYPE_KNOCKOUT, OTC_TYPE_PARACHUTE, OTC_TYPE_SUSPENSION];

export const OTC_EVENT_TYPES = [
  OTC_EVENT_TYPE_FLOOR_CEILING,
  OTC_EVENT_TYPE_KNOCKOUT,
  OTC_EVENT_TYPE_PARACHUTE,
  OTC_EVENT_TYPE_SUSPENSION,
];

export const OTC_VOLUME_TEMPLATE_FLOOR_CEILING = "{{quantity_accumulated}} {{units}} priced.";
export const OTC_VOLUME_TEMPLATE_KNOCKOUT = "{{quantity_accumulated}} {{units}} priced.";
export const OTC_VOLUME_TEMPLATE_PARACHUTE = "{{quantity_accumulated}} {{units}} priced.";
export const OTC_VOLUME_TEMPLATE_SUSPENSION = "{{quantity_accumulated}} {{units}} priced.";

// At Expiry

export const OTC_HOW_IT_WORKS_TEMPLATE_FLOOR_CEILING_AT_EXPIRY = [
  "Each period the market settles at or above the {{enhancement_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "Each period the market settles between {{enhancement_level}} and {{event_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "Each period the market settles at or below the {{event_level}}, the client sells the period's quantity at {{event_level}}.",
  "At expiration, if the market settles at or above the {{enhancement_level}}, the client sells an additional 100% (double) of the contracted volume at {{enhancement_level}}.",
];
export const OTC_HOW_IT_WORKS_TEMPLATE_KNOCKOUT_AT_EXPIRY = [
  "Each period the market settles at or above the {{enhancement_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "Each period the market settles between {{enhancement_level}} and {{event_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "If on any date between the Start Date and the Expiry Date the market touches or trades below the {{event_level}}, all further price enhancements cease. The client keeps all previously accumulated price enhanced futures lookalike.",
  "At expiration, if the market settles at or above the {{enhancement_level}}, the client sells an additional 100% (double) of the contracted volume at {{enhancement_level}}.",
];
export const OTC_HOW_IT_WORKS_TEMPLATE_PARACHUTE_AT_EXPIRY = [
  "Each period the market settles at or above the {{enhancement_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "Each period the market settles between {{enhancement_level}} and {{event_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "If on any date between the Start Date and the Expiry Date the market touches or trades below the {{event_level}}, all further price enhancements cease. The client keeps all previously accumulated price enhanced futures lookalike and sells the trade's remaining quantity at {{event_level}}.",
];
export const OTC_HOW_IT_WORKS_TEMPLATE_SUSPENSION_AT_EXPIRY = [
  "Each period the market settles at or above the {{enhancement_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "Each period the market settles between {{enhancement_level}} and {{event_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "Each period the market settles at or below the {{event_level}}, nothing is priced.",
  "At expiration, if the market settles at or above the {{enhancement_level}}, the client sells an additional 100% (double) of the contracted volume at {{enhancement_level}}",
];

// Per Period

export const OTC_HOW_IT_WORKS_TEMPLATE_FLOOR_CEILING_PER_PERIOD = [
  "Each period the market settles at or above the {{enhancement_level}}, the client sells twice the period's quantity at {{enhancement_level}}.",
  "Each period the market settles between {{enhancement_level}} and {{event_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "Each period the market settles at or below the {{event_level}}, the client sells the period's quantity at {{event_level}}.",
];
export const OTC_HOW_IT_WORKS_TEMPLATE_KNOCKOUT_PER_PERIOD = [
  "Each period the market settles at or above the {{enhancement_level}}, the client sells twice the period's quantity at {{enhancement_level}}.",
  "Each period the market settles between {{enhancement_level}} and {{event_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "If on any date between the Start Date and the Expiry Date the market touches or trades below the {{event_level}}, all further price enhancements cease. The client keeps all previously accumulated price enhanced futures lookalike.",
];
export const OTC_HOW_IT_WORKS_TEMPLATE_PARACHUTE_PER_PERIOD = [
  "Each period the market settles at or above the {{enhancement_level}}, the client sells twice the period's quantity at {{enhancement_level}}.",
  "Each period the market settles between {{enhancement_level}} and {{event_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "If on any date between the Start Date and the Expiry Date the market touches or trades below the {{event_level}}, all further price enhancements cease. The client keeps all previously accumulated price enhanced futures lookalike and sells the trade's remaining quantity at {{event_level}}.",
];
export const OTC_HOW_IT_WORKS_TEMPLATE_SUSPENSION_PER_PERIOD = [
  "Each period the market settles at or above the {{enhancement_level}}, the client sells twice the period's quantity at {{enhancement_level}}.",
  "Each period the market settles between {{enhancement_level}} and {{event_level}}, the client sells the period's quantity at {{enhancement_level}}.",
  "Each period the market settles at or below the {{event_level}}, nothing is priced.",
];

export const OTC_PRODUCTS = [
  {
    product: OTC_PRODUCT_FLOOR_CEILING,
    type: OTC_TYPE_FLOOR_CEILING,
    event_type: OTC_EVENT_TYPE_FLOOR_CEILING,
    volume_template: OTC_VOLUME_TEMPLATE_FLOOR_CEILING,
    how_it_works_template: OTC_HOW_IT_WORKS_TEMPLATE_FLOOR_CEILING_PER_PERIOD,
    how_it_works_at_expiry_template: OTC_HOW_IT_WORKS_TEMPLATE_FLOOR_CEILING_AT_EXPIRY,
    component: OTC_PRODUCT_FLOOR_CEILING,
  },
  {
    product: OTC_PRODUCT_KNOCKOUT,
    type: OTC_TYPE_KNOCKOUT,
    event_type: OTC_EVENT_TYPE_KNOCKOUT,
    volume_template: OTC_VOLUME_TEMPLATE_KNOCKOUT,
    how_it_works_template: OTC_HOW_IT_WORKS_TEMPLATE_KNOCKOUT_PER_PERIOD,
    how_it_works_at_expiry_template: OTC_HOW_IT_WORKS_TEMPLATE_KNOCKOUT_AT_EXPIRY,
    component: OTC_PRODUCT_KNOCKOUT,
  },
  {
    product: OTC_PRODUCT_PARACHUTE,
    type: OTC_TYPE_PARACHUTE,
    event_type: OTC_EVENT_TYPE_PARACHUTE,
    volume_template: OTC_VOLUME_TEMPLATE_PARACHUTE,
    how_it_works_template: OTC_HOW_IT_WORKS_TEMPLATE_PARACHUTE_PER_PERIOD,
    how_it_works_at_expiry_template: OTC_HOW_IT_WORKS_TEMPLATE_PARACHUTE_AT_EXPIRY,
    component: OTC_PRODUCT_PARACHUTE,
  },
  {
    product: OTC_PRODUCT_SUSPENSION,
    type: OTC_TYPE_SUSPENSION,
    event_type: OTC_EVENT_TYPE_SUSPENSION,
    volume_template: OTC_VOLUME_TEMPLATE_SUSPENSION,
    how_it_works_template: OTC_HOW_IT_WORKS_TEMPLATE_SUSPENSION_PER_PERIOD,
    how_it_works_at_expiry_template: OTC_HOW_IT_WORKS_TEMPLATE_SUSPENSION_AT_EXPIRY,
    component: OTC_PRODUCT_SUSPENSION,
  },
];

export const getOTCProduct = (row) => {
  // Find by "type", "event_type"
  const productType = OTC_PRODUCTS.find((p) => p.type === row.type && p.event_type === row.event_type);

  return productType;
};
